import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { Badge, LoadingSpinner, Table, TextLink } from '@/shared/components';
import type { DocumentTemplateSummary } from '../types/documentTemplates.types';
import { buildDealerNameMap, useDealersByIds } from '@/features/dealers/hooks/useDealersByIds';
import { usePermissions } from '@/features/auth';
import { TemplateOwnerCell } from './TemplateOwnerCell';
import { DOCUMENT_TYPE_LABELS } from '../utils/documentTypeLabels';
import { formatDay } from '../utils/versionLabels';
import { templateTabPath } from '../utils/templateTabs';

interface Props {
  data: DocumentTemplateSummary[];
  isLoading?: boolean;
  emptyMessage?: string;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
}

const detailPath = (template: DocumentTemplateSummary) => templateTabPath(template.id, 'details');

function TemplateNameCell({ template }: { template: DocumentTemplateSummary }) {
  return (
    <div className="space-y-0.5">
      <TextLink to={detailPath(template)} color="neutral" className="font-semibold">
        {template.name}
      </TextLink>
      <p className="text-mp-muted">{template.description || 'No description'}</p>
      <p className="font-mono text-xs text-mp-muted">{template.key}</p>
    </div>
  );
}

function TemplateStatusCell({ template }: { template: DocumentTemplateSummary }) {
  return (
    <div className="flex flex-wrap gap-2">
      {template.published ? (
        <Badge variant="success">Live v{template.published.versionNumber}</Badge>
      ) : (
        <Badge variant="outline">Not published</Badge>
      )}
      {template.hasDraft && <Badge variant="warning">Draft</Badge>}
    </div>
  );
}

export function DocumentTemplatesDataTable({ data, isLoading = false, emptyMessage, sorting, onSortingChange }: Props) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const ownerDealerIds = useMemo(
    () => data.map((template) => template.ownerDealerId).filter((id): id is string => id !== null),
    [data],
  );
  const dealersQuery = useDealersByIds(ownerDealerIds, hasPermission({ resource: 'dealer', action: 'browse' }));
  const dealerNames = useMemo(() => buildDealerNameMap(dealersQuery.data), [dealersQuery.data]);

  const columns = useMemo<ColumnDef<DocumentTemplateSummary>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Template',
        cell: (info) => <TemplateNameCell template={info.row.original} />,
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: (info) => DOCUMENT_TYPE_LABELS[info.row.original.documentType],
      },
      {
        id: 'owner',
        header: 'Owner',
        enableSorting: false,
        cell: (info) => (
          <TemplateOwnerCell
            template={info.row.original}
            dealerNames={dealerNames}
            isResolvingDealers={dealersQuery.isLoading}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: (info) => <TemplateStatusCell template={info.row.original} />,
      },
      {
        id: 'updated_at',
        accessorKey: 'updatedAt',
        header: 'Updated',
        sortDescFirst: true,
        cell: (info) => formatDay(info.row.original.updatedAt),
      },
      {
        id: 'open',
        enableSorting: false,
        header: () => <span className="sr-only">Open</span>,
        cell: () => <ChevronRight aria-hidden="true" className="size-4 text-mp-muted" />,
      },
    ],
    [dealerNames, dealersQuery.isLoading],
  );

  if (isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading templates..." />;
  }

  return (
    <Table<DocumentTemplateSummary>
      data={data}
      columns={columns}
      framed={false}
      sorting={sorting}
      onSortingChange={onSortingChange}
      emptyMessage={emptyMessage}
      onRowClick={(template) => navigate(detailPath(template))}
    />
  );
}
